import { Ionicons } from '@expo/vector-icons';
import { router, type Href } from 'expo-router';
import React, { useState } from 'react';
import { ActivityIndicator, Image, Platform, Pressable, View } from 'react-native';

import { EditPaymentModal } from '@/components/profile/edit-payment-modal';
import { PlanHero } from '@/components/settings/plan-hero';
import { BecomeTeamSheet } from '@/components/team/become-team-sheet';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PageLayout } from '@/components/ui/page-layout';
import { FEATURES } from '@/constants/feature-flags';
import { getAccountStatusLabel } from '@/content/payments';
import { useRestartInterview } from '@/hooks/use-assistant';
import { canManagePaymentAccount, useMembership } from '@/hooks/use-membership';
import { usePaymentAccount } from '@/hooks/use-payment-account';
import { usePlanFeature } from '@/hooks/use-plan-feature';
import { useProviderProfile } from '@/hooks/use-provider';
import { useTeamPlanAvailable } from '@/hooks/use-team-pricing';
import { useActingProviderStore } from '@/store/actingProviderStore';
import { useAuthStore } from '@/store/authStore';
import { useThemeStore } from '@/store/themeStore';
import { useToastStore } from '@/store/toastStore';

const THEME_LABEL = { system: 'Igual ao aparelho', light: 'Claro', dark: 'Escuro' } as const;

function goBack() {
  if (router.canGoBack()) {
    router.back();
    return;
  }
  router.replace('/(tabs)/(provider)/profile' as never);
}

function SettingsGroup({ title, children }: { title: string; children: React.ReactNode }) {
  const rows = React.Children.toArray(children).filter(Boolean);
  if (rows.length === 0) return null;
  return (
    <View className="gap-2">
      <ThemedText className="px-1 font-semibold text-sm text-neutral-500">{title}</ThemedText>
      <Card className="overflow-hidden p-0">
        {rows.map((row, i) => (
          <View key={i} className={i > 0 ? 'border-t border-border' : undefined}>
            {row}
          </View>
        ))}
      </Card>
    </View>
  );
}

function SettingsRow({
  icon,
  title,
  subtitle,
  value,
  onPress,
  disabled,
  testID,
}: {
  icon: React.ComponentProps<typeof Ionicons>['name'];
  title: string;
  subtitle?: string;
  value?: string | null;
  onPress: () => void;
  disabled?: boolean;
  testID?: string;
}) {
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      testID={testID}
      className="min-h-14 flex-row items-center gap-3 px-4 py-3 active:bg-neutral-100"
    >
      <View className="h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 dark:bg-primary">
        <Ionicons className="text-primary dark:text-white" name={icon} size={18} />
      </View>
      <View className="min-w-0 flex-1">
        <ThemedText className="font-medium text-[15px] leading-5 text-foreground">
          {title}
        </ThemedText>
        {subtitle ? (
          <ThemedText className="text-xs leading-4 text-neutral-500">{subtitle}</ThemedText>
        ) : null}
      </View>
      {disabled ? (
        <ActivityIndicator size="small" />
      ) : value ? (
        <ThemedText numberOfLines={1} className="max-w-[45%] text-sm text-neutral-500">
          {value}
        </ThemedText>
      ) : null}
      <Ionicons className="text-neutral-400" name="chevron-forward" size={18} />
    </Pressable>
  );
}

/**
 * Configurações do prestador: plano no topo, o resto em lista agrupada. Cada
 * linha mostra o estado atual à direita e leva para onde aquilo se ajusta —
 * Agenda e Recebimento abrem as telas próprias, sem duplicar nada aqui. O
 * Perfil fica só com a identidade do negócio.
 */
export function SettingsScreen() {
  const { user, deleteAccount, logout } = useAuthStore();
  const { addToast } = useToastStore();
  const themePreference = useThemeStore((s) => s.preference);
  const acting = useActingProviderStore((s) => s.acting);
  const isActing = !!acting;

  const { can, isSolo, isOwner, membership } = useMembership();
  const teamPlanAvailable = useTeamPlanAvailable();
  const canManagePayment = canManagePaymentAccount(membership, isActing);
  const { data: providerData, refetch: refetchProvider } = useProviderProfile();
  const { data: paymentAccountData } = usePaymentAccount({ enabled: canManagePayment });
  const paymentAccount = paymentAccountData?.account ?? null;

  const restartInterview = useRestartInterview();
  const reconfigurePlan = usePlanFeature('RECONFIGURE');

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showBecomeTeam, setShowBecomeTeam] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  /**
   * Reabre a entrevista completa. Não desfaz nada: o apply é aditivo e
   * idempotente, então refazer completa o que faltou sem apagar o que existe.
   */
  const handleRestartInterview = async () => {
    if (!reconfigurePlan.guard()) return;
    try {
      await restartInterview.mutateAsync();
      router.push('/onboarding');
    } catch {
      addToast('Não deu para reabrir a configuração agora.', 'error');
    }
  };

  const confirmDeleteAccount = () => {
    setIsDeleting(true);
    void deleteAccount()
      .catch(() => {})
      .finally(() => {
        setIsDeleting(false);
        setShowDeleteModal(false);
      });
  };

  const displayName = providerData?.title?.trim() || user?.username || 'Você';
  const contact = user?.email || user?.phone || null;
  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');

  const hasManualPayment = !!(
    providerData?.defaultPixCopyPaste ||
    providerData?.defaultPaymentLink ||
    providerData?.defaultBankDetails?.bankName
  );

  const showPlan = FEATURES.STRIPE_SUBSCRIPTIONS && can('billing') && !isActing;
  const push = (href: string) => router.push(href as Href);

  return (
    <PageLayout title="Configurações" onBack={goBack}>
      <View className="w-full max-w-2xl gap-6 self-center">
        <Card className="overflow-hidden p-0">
          <Pressable
            onPress={() => push('/(tabs)/(provider)/profile')}
            accessibilityRole="button"
            className="flex-row items-center gap-3 p-4 active:bg-neutral-100"
          >
            {user?.image ? (
              <Image
                source={{ uri: user.image }}
                aria-hidden
                style={{ width: 48, height: 48, borderRadius: 24 }}
              />
            ) : (
              <View className="h-12 w-12 items-center justify-center rounded-full bg-primary">
                <ThemedText className="font-semibold text-lg text-white">
                  {initials || 'U'}
                </ThemedText>
              </View>
            )}
            <View className="min-w-0 flex-1">
              <ThemedText numberOfLines={1} className="font-semibold text-base text-foreground">
                {displayName}
              </ThemedText>
              {contact ? (
                <ThemedText numberOfLines={1} className="text-sm text-neutral-500">
                  {contact}
                </ThemedText>
              ) : null}
            </View>
            <ThemedText className="text-sm text-neutral-500">Ver perfil</ThemedText>
            <Ionicons className="text-neutral-400" name="chevron-forward" size={18} />
          </Pressable>
        </Card>

        {showPlan && <PlanHero />}

        <SettingsGroup title="Seu negócio">
          {!isActing && (
            <SettingsRow
              icon="sparkles-outline"
              title="Reconfigurar meu negócio"
              subtitle="Nada do que já está no ar é apagado."
              onPress={() => void handleRestartInterview()}
              disabled={restartInterview.isPending}
            />
          )}
          {isOwner && isSolo && teamPlanAvailable && !isActing && (
            <SettingsRow
              icon="people-outline"
              title="Montar equipe"
              subtitle="Cada profissional com agenda e login próprios."
              onPress={() => setShowBecomeTeam(true)}
            />
          )}
          {can('team') && (
            <SettingsRow
              icon="people-outline"
              title="Equipe"
              value={membership?.organizationName ?? null}
              onPress={() => push('/(tabs)/(provider)/team/settings')}
            />
          )}
        </SettingsGroup>

        <SettingsGroup title="Como você trabalha">
          <SettingsRow
            icon="calendar-outline"
            title="Agenda"
            subtitle="Horários, intervalos e folgas"
            onPress={() => push('/(tabs)/(provider)/schedule/settings')}
          />
          {canManagePayment && paymentAccount && (
            <SettingsRow
              icon="card-outline"
              title="Recebimento"
              value={getAccountStatusLabel(paymentAccount.status)}
              onPress={() => push('/(tabs)/(provider)/payments/settings')}
            />
          )}
          {can('ownPayment') && (
            <SettingsRow
              icon="qr-code-outline"
              title="Pix e dados para pagar"
              subtitle="Vão na cobrança quando o cliente paga direto a você"
              value={hasManualPayment ? 'Salvo' : 'Não informado'}
              onPress={() => setShowPaymentModal(true)}
            />
          )}
        </SettingsGroup>

        <SettingsGroup title="App">
          <SettingsRow
            icon="contrast-outline"
            title="Aparência"
            value={THEME_LABEL[themePreference]}
            onPress={() => push('/(tabs)/(provider)/settings/appearance')}
            testID="settings-appearance"
          />
          <SettingsRow
            icon="document-text-outline"
            title="Termos de uso"
            onPress={() => push('/terms')}
          />
          <SettingsRow
            icon="shield-checkmark-outline"
            title="Privacidade"
            onPress={() => push('/privacidade')}
          />
        </SettingsGroup>

        <SettingsGroup title="Ajuda">
          <SettingsRow
            icon="help-buoy-outline"
            title="Falar com o suporte"
            onPress={() => push('/suporte')}
          />
          {!isActing && (
            <SettingsRow
              icon="rocket-outline"
              title="Rever introdução"
              onPress={() => push('/onboarding')}
            />
          )}
        </SettingsGroup>

        <View className="gap-2">
          <Button variant="outline" size="lg" onPress={() => logout()} className="border-error-200">
            <View className="flex-row items-center gap-2">
              <Ionicons className="text-error-500" name="log-out-outline" size={20} />
              <ThemedText className="font-medium text-sm text-error-500">Sair da conta</ThemedText>
            </View>
          </Button>
          {!isActing && (
            <Button
              variant="ghost"
              size="lg"
              onPress={() => setShowDeleteModal(true)}
              disabled={isDeleting}
            >
              <View className="flex-row items-center gap-2">
                <Ionicons className="text-error-500" name="trash-outline" size={18} />
                <ThemedText className="font-medium text-sm text-error-500">
                  {isDeleting ? 'Excluindo...' : 'Excluir conta'}
                </ThemedText>
              </View>
            </Button>
          )}
        </View>
      </View>

      <BecomeTeamSheet open={showBecomeTeam} onClose={() => setShowBecomeTeam(false)} />

      <EditPaymentModal
        open={showPaymentModal}
        onOpenChange={setShowPaymentModal}
        onSuccess={() => {
          refetchProvider();
          setShowPaymentModal(false);
        }}
      />

      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal} dismissable={!isDeleting}>
        <DialogContent className="rounded-2xl">
          <DialogHeader className="gap-2">
            <View className="mb-1 h-12 w-12 items-center justify-center rounded-full bg-error-50 dark:bg-error-500">
              <Ionicons className="text-error-500 dark:text-white" name="trash-outline" size={22} />
            </View>
            <DialogTitle>Excluir conta</DialogTitle>
            <DialogDescription>
              Isso apaga sua conta e todos os dados associados. Não dá para desfazer.
              {Platform.OS === 'ios'
                ? ' Se você assina pela App Store, cancele a assinatura em Ajustes > Apple ID > Assinaturas — excluir a conta não cancela a cobrança da Apple.'
                : ''}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="mt-6 flex-row gap-2">
            <Button
              variant="outline"
              size="md"
              disabled={isDeleting}
              onPress={() => setShowDeleteModal(false)}
              className="flex-1"
            >
              <ThemedText className="font-semibold text-sm">Cancelar</ThemedText>
            </Button>
            <Button
              variant="destructive"
              size="md"
              disabled={isDeleting}
              onPress={confirmDeleteAccount}
              className="flex-1"
            >
              <View className="flex-row items-center gap-2">
                {isDeleting ? <ActivityIndicator size="small" color="#fff" /> : null}
                <ThemedText className="font-semibold text-sm text-white">
                  {isDeleting ? 'Excluindo...' : 'Excluir conta'}
                </ThemedText>
              </View>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}
